// src/components/pages/Movimento/createOrEdit/modules/distribuicaoFiscal/useDistribuicaoFiscalModule.js

import { useCallback, useEffect, useState } from 'react';

import * as DistribuicaoFiscalAPI from '../../../../../../api/distribuicaoFiscal';
import * as MovimentoAPI from '../../../../../../api/movimento';

import { useMRTState } from '../../../../../../hooks/useMRTState';

export function useDistribuicaoFiscalModule({
    empresaId,
    is_cEANTrib,
    parametro,
    mrtResumos,
    mrtDocumentos,
    setFlashMessage,
    onImportarResumo,
}) {
    const [open, setOpen] = useState(false);

    const [visualizacao, setVisualizacao] = useState('RESUMOS');

    const [resumos, setResumos] = useState([]);

    const [totalResumos, setTotalResumos] = useState(0);

    const [limiteDownloadLote, setLimiteDownloadLote] = useState(1);

    const [documentosSemMovimento, setDocumentosSemMovimento] = useState([]);

    const [totalDocumentosSemMovimento, setTotalDocumentosSemMovimento] = useState(0);

    const [isLoading, setIsLoading] = useState(false);

    const [isRefetching, setIsRefetching] = useState(false);

    const [error, setError] = useState(null);

    const [isImporting, setIsImporting] = useState(false);

    const [isDownloadingBatch, setIsDownloadingBatch] = useState(false);

    const [progressoDownload, setProgressoDownload] = useState({
        atual: 0,
        total: 0,
    });
    /*
     * Cada aba possui seu próprio estado MRT,
     * criado e configurado pelo FormContainer.
     */
    const {
        pagination: paginationResumos,
        setPagination: setPaginationResumos,

        columnFilters: columnFiltersResumos,
        columnFilterFns: columnFilterFnsResumos,
        sorting: sortingResumos,

        globalFilter: globalFilterResumos,
        setGlobalFilter: setGlobalFilterResumos,

        setRowCount: setRowCountResumos,
    } = mrtResumos;

    const {
        pagination: paginationDocumentos,
        setPagination: setPaginationDocumentos,

        columnFilters: columnFiltersDocumentos,
        columnFilterFns: columnFilterFnsDocumentos,
        sorting: sortingDocumentos,

        globalFilter: globalFilterDocumentos,
        setGlobalFilter: setGlobalFilterDocumentos,

        setRowCount: setRowCountDocumentos,
    } = mrtDocumentos;

    /*
     * Mantém compatibilidade temporária com o restante
     * do módulo enquanto migramos cada função.
     */
    const mrtAtivo = visualizacao === 'XMLS_BAIXADOS' ? mrtDocumentos : mrtResumos;

    const carregarResumos = useCallback(
        async ({ refetch = false } = {}) => {
            const empresaIdNormalizado = Number(empresaId);

            if (!Number.isInteger(empresaIdNormalizado) || empresaIdNormalizado <= 0) {
                setResumos([]);
                setTotalResumos(0);
                setRowCountResumos(0);
                setLimiteDownloadLote(1);
                return;
            }

            if (refetch) {
                setIsRefetching(true);
            } else {
                setIsLoading(true);
            }

            setError(null);

            try {
                const response = await DistribuicaoFiscalAPI.listarResumosPendentes({
                    empresaId: empresaIdNormalizado,

                    // Contrato MRT padrão
                    start: paginationResumos.pageIndex * paginationResumos.pageSize,

                    size: paginationResumos.pageSize,

                    filters: JSON.stringify(columnFiltersResumos ?? []),

                    filtersMode: JSON.stringify(columnFilterFnsResumos ?? {}),

                    sorting: JSON.stringify(sortingResumos ?? []),

                    globalFilter: globalFilterResumos ?? '',
                });

                const resultado = response?.data ?? response ?? {};

                const limiteRecebido = Number(resultado.limiteDownloadLote);

                setLimiteDownloadLote(Number.isInteger(limiteRecebido) && limiteRecebido > 0 ? limiteRecebido : 1);

                setResumos(Array.isArray(resultado.rows) ? resultado.rows : []);

                const total = Number(resultado.total) || 0;

                setTotalResumos(total);

                setRowCountResumos(total);

                return resultado;
            } catch (error) {
                console.error('Erro ao carregar resumos fiscais:', error);

                setResumos([]);

                setTotalResumos(0);

                setLimiteDownloadLote(1);

                setRowCountResumos(0);

                setError(error);

                if (typeof setFlashMessage === 'function') {
                    setFlashMessage(
                        error?.response?.data?.message || error?.message || 'Não foi possível carregar as notas disponíveis.',
                        'error'
                    );
                }

                return null;
            } finally {
                setIsLoading(false);
                setIsRefetching(false);
            }
        },
        [
            empresaId,

            paginationResumos.pageIndex,
            paginationResumos.pageSize,
            columnFiltersResumos,
            columnFilterFnsResumos,
            sortingResumos,
            globalFilterResumos,

            setRowCountResumos,

            setFlashMessage,
        ]
    );

    const abrir = useCallback(() => {
        const empresaIdNormalizado = Number(empresaId);

        if (!Number.isInteger(empresaIdNormalizado) || empresaIdNormalizado <= 0) {
            if (typeof setFlashMessage === 'function') {
                setFlashMessage('Selecione a empresa antes de consultar as notas disponíveis.', 'warning');
            }

            return;
        }

        setOpen(true);
    }, [empresaId, setFlashMessage]);

    const fechar = useCallback(() => {
        setOpen(false);
    }, []);

    const carregarDocumentoSemMovimento = useCallback(
        async (documento) => {
            const documentoFiscalDistribuidoId = Number(documento?.documentoFiscalDistribuidoId || documento?.id);

            if (!Number.isInteger(documentoFiscalDistribuidoId) || documentoFiscalDistribuidoId <= 0) {
                setFlashMessage('O XML selecionado é inválido.', 'warning');

                return false;
            }

            if (typeof onImportarResumo !== 'function') {
                setFlashMessage('A rotina de aplicação do XML não está disponível.', 'error');

                return false;
            }

            setIsImporting(true);

            try {
                const response = await DistribuicaoFiscalAPI.prepararDocumentoSemMovimento({
                    documentoFiscalDistribuidoId,

                    is_cEANTrib: is_cEANTrib === true,
                });

                if (response?.type !== 'success' || !response?.data) {
                    setFlashMessage(
                        response?.message || 'Não foi possível carregar o XML persistido.',

                        'error',
                        8
                    );

                    return false;
                }

                /*
                 * Reutiliza exatamente a mesma aplicação de
                 * cabeçalho e itens usada pelo fluxo da SEFAZ.
                 */
                const aplicado = await onImportarResumo(response, documento);

                if (aplicado === false) {
                    setFlashMessage(
                        'O XML foi lido, mas não pôde ser aplicado ao Movimento.',

                        'error',
                        8
                    );

                    return false;
                }

                setFlashMessage(
                    response?.message || 'XML persistido carregado com sucesso.',

                    'success'
                );

                setOpen(false);

                return true;
            } catch (error) {
                console.error('Erro ao carregar documento fiscal persistido:', error);

                setFlashMessage(
                    error?.response?.data?.message || error?.message || 'Não foi possível carregar o XML persistido.',

                    'error',
                    8
                );

                return false;
            } finally {
                setIsImporting(false);
            }
        },
        [is_cEANTrib, onImportarResumo, setFlashMessage]
    );
    const carregarDocumentosSemMovimento = useCallback(
        async ({ refetch = false } = {}) => {
            const empresaIdNormalizado = Number(empresaId);

            if (!Number.isInteger(empresaIdNormalizado) || empresaIdNormalizado <= 0) {
                setDocumentosSemMovimento([]);

                setTotalDocumentosSemMovimento(0);

                setRowCountDocumentos(0);

                return null;
            }

            if (refetch) {
                setIsRefetching(true);
            } else {
                setIsLoading(true);
            }

            setError(null);

            try {
                const response = await DistribuicaoFiscalAPI.listarDocumentosSemMovimento({
                    empresaId: empresaIdNormalizado,

                    // Contrato MRT padrão
                    start: paginationDocumentos.pageIndex * paginationDocumentos.pageSize,

                    size: paginationDocumentos.pageSize,

                    filters: JSON.stringify(columnFiltersDocumentos ?? []),

                    filtersMode: JSON.stringify(columnFilterFnsDocumentos ?? {}),

                    sorting: JSON.stringify(sortingDocumentos ?? []),

                    globalFilter: globalFilterDocumentos ?? '',
                });

                const resultado = response?.data ?? response ?? {};

                const rows = Array.isArray(resultado.rows) ? resultado.rows : [];

                const total = Number(resultado.total) || 0;

                setDocumentosSemMovimento(rows);

                setTotalDocumentosSemMovimento(total);

                setRowCountDocumentos(total);

                return resultado;
            } catch (error) {
                console.error('Erro ao carregar XMLs sem Movimento:', error);

                setDocumentosSemMovimento([]);
                setTotalDocumentosSemMovimento(0);
                setRowCountDocumentos(0);
                setError(error);

                if (typeof setFlashMessage === 'function') {
                    setFlashMessage(
                        error?.response?.data?.message || error?.message || 'Não foi possível carregar os XMLs baixados.',

                        'error'
                    );
                }

                return null;
            } finally {
                setIsLoading(false);
                setIsRefetching(false);
            }
        },
        [
            empresaId,

            paginationDocumentos.pageIndex,
            paginationDocumentos.pageSize,
            columnFiltersDocumentos,
            columnFilterFnsDocumentos,
            sortingDocumentos,
            globalFilterDocumentos,

            setRowCountDocumentos,

            setFlashMessage,
        ]
    );

    const atualizar = useCallback(() => {
        if (visualizacao === 'XMLS_BAIXADOS') {
            return carregarDocumentosSemMovimento({
                refetch: true,
            });
        }

        return carregarResumos({
            refetch: true,
        });
    }, [visualizacao, carregarDocumentosSemMovimento, carregarResumos]);

    const importarResumo = useCallback(
        async (resumo) => {
            if (!resumo?.id) {
                setFlashMessage('O resumo fiscal selecionado é inválido.', 'warning');

                return false;
            }

            if (typeof onImportarResumo !== 'function') {
                setFlashMessage('A rotina de aplicação do XML não está disponível.', 'error');

                return false;
            }

            setIsImporting(true);

            try {
                const response = await MovimentoAPI.importarDocumentoFiscalDistribuido({
                    distribuicaoFiscalDocumentoResumoId: resumo.id,

                    is_cEANTrib: is_cEANTrib === true,
                });

                if (response?.type === 'error') {
                    setFlashMessage(response?.message || 'Não foi possível obter o XML da nota fiscal.', 'error', 8);

                    return false;
                }

                /*
                 * A Ciência foi registrada, mas o procNFe pode
                 * ainda não estar disponível imediatamente.
                 *
                 * Fecha somente a confirmação e mantém a lista aberta.
                 */
                if (response?.type === 'info' || response?.data?.disponivel === false) {
                    setFlashMessage(response?.message || 'O XML ainda não está disponível na SEFAZ.', 'info', 8);

                    await carregarResumos({
                        refetch: true,
                    });

                    return true;
                }

                if (response?.type !== 'success' || !response?.data) {
                    setFlashMessage(response?.message || 'A SEFAZ não retornou dados válidos.', 'error', 8);

                    return false;
                }

                /*
                 * O callback não consulta mais o XML.
                 * Ele apenas aplica nfe e preNota ao Movimento.
                 */
                const aplicado = await onImportarResumo(response, resumo);

                if (aplicado === false) {
                    setFlashMessage('O XML foi obtido, mas não pôde ser aplicado ao Movimento.', 'error', 8);

                    return false;
                }

                setFlashMessage(response?.message || 'Nota fiscal carregada com sucesso.', 'success');

                setOpen(false);

                return true;
            } catch (error) {
                console.error('Erro ao importar resumo fiscal:', error);

                setFlashMessage(
                    error?.response?.data?.message || error?.message || 'Não foi possível obter o XML da nota fiscal.',
                    'error',
                    8
                );

                return false;
            } finally {
                setIsImporting(false);
            }
        },
        [carregarResumos, is_cEANTrib, onImportarResumo, setFlashMessage]
    );
    const baixarResumosSelecionados = useCallback(
        async (resumosSelecionados = []) => {
            const resumosPorId = new Map();

            for (const resumo of resumosSelecionados) {
                const id = Number(resumo?.id);

                if (Number.isInteger(id) && id > 0) {
                    resumosPorId.set(id, resumo);
                }
            }

            const resumosValidos = [...resumosPorId.values()];

            if (!resumosValidos.length) {
                setFlashMessage('Selecione ao menos uma nota fiscal.', 'warning');

                return null;
            }

            if (resumosValidos.length > limiteDownloadLote) {
                setFlashMessage(
                    `Selecione no máximo ${limiteDownloadLote} nota(s) por lote.`,

                    'warning',

                    8
                );

                return null;
            }

            const resumoProcessamento = {
                selecionados: resumosValidos.length,

                baixados: 0,

                duplicados: 0,

                aguardandoSefaz: 0,

                erros: 0,

                naoProcessados: 0,

                interrompidoPorBloqueio: false,

                idsNaoProcessados: [],

                falhas: [],
            };

            setIsDownloadingBatch(true);

            setProgressoDownload({
                atual: 0,

                total: resumosValidos.length,
            });

            try {
                /*
                 * Processamento obrigatoriamente sequencial.
                 * Não substituir por Promise.all.
                 */
                for (let indice = 0; indice < resumosValidos.length; indice += 1) {
                    const resumo = resumosValidos[indice];

                    setProgressoDownload({
                        atual: indice + 1,

                        total: resumosValidos.length,
                    });

                    const response = await DistribuicaoFiscalAPI.baixarDocumento({
                        distribuicaoFiscalDocumentoResumoId: resumo.id,
                    });

                    const resultado = response?.data;

                    const code = String(response?.code || '')
                        .trim()
                        .toUpperCase();

                    const consultaBloqueada =
                        Number(response?.status) === 429 ||
                        [
                            'CONSULTA_CHAVE_SEFAZ_BLOQUEADA',

                            'CONSULTA_CHAVE_BLOQUEIO_LOCAL_ATIVO',

                            'CONSULTA_CHAVE_LIMITE_LOCAL_ATINGIDO',
                        ].includes(code);

                    /*
                     * No primeiro bloqueio, nenhuma nota posterior
                     * pode gerar outra requisição.
                     *
                     * A nota atual também permanece pendente porque
                     * o XML não foi obtido.
                     */
                    if (consultaBloqueada) {
                        resumoProcessamento.erros += 1;

                        resumoProcessamento.falhas.push({
                            resumoId: resumo.id,

                            chaveAcesso: resumo.chaveAcesso || null,

                            message: response?.message || 'Consultas temporariamente bloqueadas.',

                            code: response?.code || null,
                        });

                        resumoProcessamento.interrompidoPorBloqueio = true;

                        resumoProcessamento.idsNaoProcessados = resumosValidos
                            .slice(indice)
                            .map((item) => Number(item.id))
                            .filter((id) => Number.isInteger(id) && id > 0);

                        resumoProcessamento.naoProcessados = resumoProcessamento.idsNaoProcessados.length;

                        break;
                    }

                    if (response?.type === 'success' && resultado?.disponivel === true) {
                        const duplicado =
                            resultado?.persistencia?.duplicado === true || resultado?.documentoOperacional?.duplicado === true;

                        if (duplicado) {
                            resumoProcessamento.duplicados += 1;
                        } else {
                            resumoProcessamento.baixados += 1;
                        }

                        continue;
                    }

                    if (response?.type === 'info' || resultado?.disponivel === false) {
                        resumoProcessamento.aguardandoSefaz += 1;

                        continue;
                    }

                    resumoProcessamento.erros += 1;

                    resumoProcessamento.falhas.push({
                        resumoId: resumo.id,

                        chaveAcesso: resumo.chaveAcesso || null,

                        message: response?.message || 'Erro ao baixar o XML.',

                        code: response?.code || null,
                    });
                }

                await Promise.all([
                    carregarResumos({
                        refetch: true,
                    }),

                    carregarDocumentosSemMovimento({
                        refetch: true,
                    }),
                ]);

                const mensagem = [
                    resumoProcessamento.interrompidoPorBloqueio
                        ? 'Download interrompido para proteger o acesso à SEFAZ.'
                        : 'Download concluído.',

                    `Baixados: ${resumoProcessamento.baixados}.`,

                    `Já existentes: ${resumoProcessamento.duplicados}.`,

                    `Aguardando SEFAZ: ${resumoProcessamento.aguardandoSefaz}.`,

                    `Erros: ${resumoProcessamento.erros}.`,

                    resumoProcessamento.naoProcessados > 0 ? `Não processados: ${resumoProcessamento.naoProcessados}.` : null,
                ]
                    .filter(Boolean)
                    .join(' ');

                const tipoMensagem = resumoProcessamento.interrompidoPorBloqueio
                    ? 'warning'
                    : resumoProcessamento.erros === resumoProcessamento.selecionados
                      ? 'error'
                      : resumoProcessamento.erros > 0
                        ? 'warning'
                        : resumoProcessamento.aguardandoSefaz > 0
                          ? 'info'
                          : 'success';

                setFlashMessage(
                    mensagem,

                    tipoMensagem,

                    10
                );

                return resumoProcessamento;
            } finally {
                setIsDownloadingBatch(false);
            }
        },
        [carregarResumos, carregarDocumentosSemMovimento, limiteDownloadLote, setFlashMessage]
    );
    const alterarVisualizacao = useCallback((novaVisualizacao) => {
        if (novaVisualizacao !== 'RESUMOS' && novaVisualizacao !== 'XMLS_BAIXADOS') {
            return;
        }

        setError(null);

        setVisualizacao(novaVisualizacao);
    }, []);

    /*
     * Carrega somente quando o modal estiver aberto.
     *
     * Alterações de paginação também executam uma nova consulta,
     * pois carregarResumos muda com pageIndex/pageSize.
     */
    useEffect(() => {
        if (!open) return;

        if (visualizacao === 'XMLS_BAIXADOS') {
            carregarDocumentosSemMovimento();

            return;
        }

        carregarResumos();
    }, [open, visualizacao, carregarResumos, carregarDocumentosSemMovimento]);

    /*
     * Se a empresa mudar, limpa os dados pertencentes à empresa anterior.
     * Não interfere no restante do Movimento.
     */
    useEffect(() => {
        setOpen(false);
        setResumos([]);
        setTotalResumos(0);
        setError(null);
        setPaginationResumos((prev) => ({
            ...prev,
            pageIndex: 0,
        }));

        setPaginationDocumentos((prev) => ({
            ...prev,
            pageIndex: 0,
        }));

        setGlobalFilterResumos('');

        setGlobalFilterDocumentos('');

        setRowCountResumos(0);

        setRowCountDocumentos(0);

        setVisualizacao('RESUMOS');

        setDocumentosSemMovimento([]);

        setTotalDocumentosSemMovimento(0);
    }, [
        empresaId,

        setPaginationResumos,
        setPaginationDocumentos,

        setGlobalFilterResumos,
        setGlobalFilterDocumentos,

        setRowCountResumos,
        setRowCountDocumentos,
    ]);

    return {
        mrt: mrtAtivo,

        parametro,

        state: {
            open,

            visualizacao,

            resumos,
            totalResumos,

            limiteDownloadLote,

            documentosSemMovimento,
            totalDocumentosSemMovimento,

            pagination: mrtAtivo.pagination,
            error,

            progressoDownload,
        },

        flags: {
            isLoading,
            isRefetching,
            isImporting,
            isError: !!error,
            isDownloadingBatch,
        },

        actions: {
            abrir,
            fechar,
            atualizar,

            carregarResumos,
            carregarDocumentosSemMovimento,

            importarResumo,
            carregarDocumentoSemMovimento,

            alterarVisualizacao,
            setPagination: mrtAtivo.setPagination,

            baixarResumosSelecionados,
        },
    };
}
